/**
 * Batch B — reálný seed (Fidelity Target 2040, INVESTIKA realitní, MONETIKA, EFEKTIKA).
 * Typ řádku je shodný s Batch A (`BatchASeedRow` v `base-funds-batch-a.seed.ts`).
 */

import type { BatchASeedRow } from "./base-funds-batch-a.seed";

export const BATCH_B_SEED_ROWS: readonly BatchASeedRow[] = [
  {
    baseFundKey: "fidelity_target_2040",
    canonicalName: "Fidelity Funds - Fidelity Target™ 2040 Fund A-Euro",
    displayName: "Fidelity Target 2040",
    provider: "Fidelity International",
    manager: "FIL Investment Management (Luxembourg) S.à r.l.",
    category: "Multi-asset fond",
    subcategory: "Target date / lifecycle 2040",
    currency: "EUR",
    isin: "LU0251119318",
    ticker: null,
    riskSRI: 5,
    goal:
      "Fond usiluje o dlouhodobý kapitálový růst pro investory, kteří plánují významně čerpat své prostředky v roce 2040.",
    strategy:
      "Týmově řízená target-date strategie s dynamickou alokací mezi akcie, dluhopisy a další třídy aktiv. V průběhu času fond automaticky snižuje rizikovost a před cílovým datem postupně přesouvá portfolio do konzervativnějších aktiv.",
    description:
      "Životní cyklus v jednom fondu: vyšší podíl akcií v delším horizontu, postupné snižování rizika směrem k roku 2040. Hodí se pro investory, kteří chtějí delegovat asset allocation na správce.",
    suitable:
      "Pro investory s dlouhým horizontem k roku 2040, kteří chtějí jednofondové řešení s automatickým snižováním rizika v čase.",
    horizon: "do roku 2040, ideálně 5+ let",
    liquidity: "Otevřený lucemburský UCITS fond; standardní obchodování dle prospektu.",
    risks:
      "Fond používá i deriváty a investuje napříč akciemi, dluhopisy a dalšími třídami aktiv včetně emerging markets. Při růstu sazeb mohou klesat dluhopisy a při poklesu trhů akciová složka výrazně kolísá.",
    minInvestment: null,
    planningRate: 6,
    officialPerformance: {
      ytd: "2.5%",
      oneYear: "6.6%",
      threeYearPA: "11.7%",
      fiveYearPA: "7.7%",
      tenYearPA: null,
      sinceInceptionPA: "5.7%",
      asOf: "2026-02-28",
    },
    benefits: [
      "Automatické snižování rizikovosti směrem k roku 2040.",
      "Široká globální alokace mezi více tříd aktiv v jednom fondu.",
      "Charakteristika: fond řeší aktivní rebalancování portfolia uvnitř produktu.",
    ],
    parameters: {
      fundLaunch: "2006-06-26",
      shareClassLaunch: "2006-06-26",
      fundSize: "€ 408m",
      fundReferenceCurrency: "EUR",
      domicile: "Luxembourg",
      legalStructure: "SICAV",
      ocf: "1.92%",
      annualManagementCharge: "1.50%",
      peerGroupUniverse: "Morningstar EAA Fund Target Date 2036 - 2040",
      annualisedVolatility3Y: "11.04%",
      sharpeRatio3Y: "0.78",
      allocationsCount: "19",
      equities: "99.60% TNA",
      fixedIncome: "0.50% TNA",
      assetAllocationNote:
        "Factsheet reports a dynamic de-risking glidepath; month-end snapshot can materially differ over time.",
    },
    topHoldings: [
      "Fidelity Japan Equity Research Enhanced UCITS ETF 5.5%",
      "NVIDIA 4.5%",
      "Apple 4.2%",
      "Alphabet 3.5%",
      "Microsoft 3.0%",
      "Amazon 2.3%",
      "Broadcom 1.7%",
      "Meta Platforms 1.7%",
      "Taiwan Semiconductor 1.6%",
      "Samsung Electronics 1.2%",
    ],
    countries: [],
    sectors: [],
    morningstarRating: null,
    awards: [],
    factsheetUrl: "https://www.fidelityinternational.com/FILPS/Documents/en/current/mf.en.gb.LU0251119318.pdf",
    factsheetAsOf: "2026-02-28",
    verifiedAt: "2026-04-05",
    logo: "/logos/funds/fidelity_target_2040.png",
    heroImage: "/report-assets/funds/fidelity_target_2040/hero.svg",
    galleryImages: [
      "/report-assets/funds/fidelity_target_2040/gallery-1.svg",
      "/report-assets/funds/fidelity_target_2040/gallery-2.svg",
      "/report-assets/funds/fidelity_target_2040/gallery-3.png",
    ],
    galleryType: "logo",
    sources: [
      {
        label: "Official Czech fund page",
        url: "https://www.fidelity.cz/fondy-a-ceny/factsheet/LU0251119318",
        kind: "landing_page",
      },
      {
        label: "Official monthly factsheet",
        url: "https://www.fidelityinternational.com/FILPS/Documents/en/current/mf.en.gb.LU0251119318.pdf",
        kind: "factsheet",
      },
      {
        label: "Official KIID / KID",
        url: "https://www.fidelityinternational.com/FDS/KIID/FF/en-gb/FF-Fidelity%20Funds%20-%20Fidelity%20Target%20TM%202040%20Fund%20A-Euro_STRD_en-gb_LU0251119318.pdf",
        kind: "kid",
      },
    ],
    assetTodo: ["hero"],
    notes: [
      "Logo: PNG v `public/logos/funds/` — při změně brand guidelines aktualizovat soubor.",
      "Galerie: `design/img-pdf/Fidelity1.svg` → `gallery-1.svg`, `design/img-pdf/Fidelity2.svg` → `gallery-2.svg`, `design/img-pdf/msci2.png` → `gallery-3.png` v `public/report-assets/funds/fidelity_target_2040/`.",
      "Morningstar peer group je veřejně uveden, ale explicitní star rating není na použitém factsheetu uveden; ponecháno null.",
      "planningRate 6 % je interní modelový předpoklad pro projekce, ne oficiální performance.",
    ],
  },
  {
    baseFundKey: "investika_realitni_fond",
    canonicalName: "INVESTIKA realitní fond, otevřený podílový fond, třída CZK",
    displayName: "INVESTIKA realitní fond",
    provider: "INVESTIKA",
    manager: "INVESTIKA, investiční společnost, a.s.",
    category: "Nemovitostní fond",
    subcategory: "Real assets / retail",
    currency: "CZK",
    isin: "CZ0008474830",
    ticker: null,
    riskSRI: 2,
    goal:
      "Fond chce dlouhodobě stabilně zhodnocovat peníze investovat do nemovitostí s přiměřeným rizikem.",
    strategy:
      "Investuje především do kvalitních kancelářských a obchodních budov. Využívá dlouhé nájemní smlouvy s navyšováním o inflaci a rozkládá riziko mezi různé typy nemovitostí, lokality a nájemce.",
    description:
      "Nemovitostní fond pro běžné investory, zaměřený na kanceláře, obchody a sklady ve střední Evropě. Výhodou je velké portfolio budov, dlouhé nájmy a nižší kolísání hodnoty než u akciových fondů.",
    suitable:
      "Pro opatrnější investory, kteří chtějí využít výnosů z komerčních nemovitostí bez toho, aby museli sami nemovitost kupovat a spravovat.",
    horizon: "5+ let",
    liquidity: "Peníze lze vybrat, ale výběr nemusí být okamžitý – fond drží nemovitosti, které nelze prodat tak rychle jako akcie nebo peníze na spořicím účtu.",
    risks:
      "Ceny nemovitostí mohou klesat, fond používá úvěry, je tu měnové riziko a peníze nelze vybrat okamžitě, protože prodej nemovitostí trvá.",
    minInvestment: "od 100 Kč",
    planningRate: 5,
    officialPerformance: {
      ytd: "0.49%",
      oneYear: "5.32%",
      threeYearPA: "5.43% (derived from official 17.18% cumulative / 3y)",
      fiveYearPA: "5.99% (derived from official 33.73% cumulative / 5y)",
      tenYearPA: null,
      sinceInceptionPA: "5.23% (derived from official 70.32% cumulative since 2015-09-16)",
      asOf: "2026-02-28",
    },
    benefits: [
      "Příjmy z nájmů jsou u značné části portfolia chráněné inflačními doložkami.",
      "Široce diverzifikované portfolio komerčních nemovitostí ve více zemích Evropy.",
      "Nízké vstupní minimum a nižší rizikovost než u typických akciových fondů.",
    ],
    parameters: {
      fundType: "Speciální fond kolektivního investování, otevřený podílový fond",
      launchDate: "2015-09-16",
      targetReturn: "4 - 6 % ročně",
      propertiesCount: "65",
      countriesCount: "6",
      minimumInvestment: "100 Kč",
      fundCapitalCzkClass: "25 419 035 268.36 CZK (2025-12-31)",
      official3YCumulative: "17.18%",
      official5YCumulative: "33.73%",
      officialSinceLaunchCumulative: "70.32%",
      inflationLinkedLeases: "Ano",
    },
    topHoldings: [
      "7R Campuses & Data Centres – Bydhošť, Poznaň, Krakov, Gdaňsk, Kielce",
      "Obchodní centrum Höfe am Brühl – Lipsko",
      "Face2Face Business Campus – Katovice",
      "Galerie Butovice – Praha 5",
      "Výrobně-logistický areál ADIENT – Skarbimierz",
      "P180 – Varšava",
    ],
    countries: [
      "Polsko 59%",
      "Česká republika 23%",
      "Německo 10%",
      "Chorvatsko 4%",
      "Rakousko 3%",
      "Španělsko 1%",
    ],
    sectors: [
      "Kanceláře 49%",
      "Logistika 22%",
      "Retail 18%",
      "Rezidence 2%",
      "Datová centra 1%",
      "Bankovní domy 1%",
      "Ostatní 8%",
    ],
    sectorsOtherBreakdown: ["Developerské a rezidenční projekty 8%"],
    morningstarRating: null,
    awards: [],
    factsheetUrl: "https://www.investika.cz/investicni-fondy/investika-realitni-fond",
    factsheetAsOf: "2026-02-28",
    verifiedAt: "2026-04-05",
    logo: "/logos/funds/investika_realitni_fond.png",
    heroImage: "/report-assets/funds/investika_realitni_fond/hero.svg",
    galleryType: "logo",
    galleryImages: [
      "/report-assets/funds/investika_realitni_fond/gallery-1.svg",
      "/report-assets/funds/investika_realitni_fond/gallery-2.svg",
      "/report-assets/funds/investika_realitni_fond/gallery-3.svg",
    ],
    sources: [
      {
        label: "Official product page",
        url: "https://www.investika.cz/investicni-fondy/investika-realitni-fond",
        kind: "landing_page",
      },
      {
        label: "Official current KID CZK",
        url: "https://www.investika.cz/files/investika-kid-czk-cz-20260211.pdf",
        kind: "kid",
      },
      {
        label: "Official statute",
        url: "https://www.investika.cz/files/20250625-statut-irf-clean.pdf",
        kind: "documents",
      },
    ],
    assetTodo: ["hero"],
    notes: [
      "Logo: PNG v `public/logos/funds/` — při změně brand guidelines aktualizovat soubor.",
      "Investika na veřejné stránce uvádí 3Y/5Y/since launch primárně jako kumulativní výkon; anualizace výše je dopočtena pro potřeby jednotného seed formátu.",
      "countries a sectors doplněny s váhami z oficiální produktové stránky investika.cz (geografická a sektorová diverzifikace); sectorsOtherBreakdown pro řádek Ostatní odpovídá popisu developerských a rezidenčních aktiv na stránce.",
      "planningRate 5 % je interní modelový předpoklad pro projekce, ne oficiální performance.",
    ],
  },
  {
    baseFundKey: "monetika",
    canonicalName: "MONETIKA, otevřený podílový fond, třída CZK",
    displayName: "MONETIKA",
    provider: "INVESTIKA",
    manager: "INVESTIKA, investiční společnost, a.s.",
    category: "Peněžní fond",
    subcategory: "Cash management / short-term reserve",
    currency: "CZK",
    isin: "CZ0008477379",
    ticker: null,
    riskSRI: 1,
    goal:
      "Fond usiluje o zhodnocení svěřených prostředků zejména prostřednictvím nástrojů peněžního trhu a souvisejících technik obhospodařování při nízkém riziku.",
    strategy:
      "MONETIKA funguje jako investiční peněženka. V aktuální strategii využívá zejména repo operace, termínované vklady a overnight depozita a výnos je navázán na dvoutýdenní repo sazbu ČNB po odečtení nákladů fondu.",
    description:
      "Fond pro krátkodobou až střednědobou rezervu a zaparkování volných peněz. Cílí na stabilnější vývoj než akciové a dluhopisové fondy a správce jej staví jako alternativu ke spořicím účtům.",
    suitable:
      "Pro konzervativní investory a pro klienty, kteří potřebují držet likvidní rezervu s rychlým přístupem k penězům.",
    horizon: "1 rok a více",
    liquidity: "Denní oceňování a obchodování; správce uvádí typicky dostupnost peněz do 3 pracovních dnů.",
    risks:
      "Nízké tržní riziko, ale stále existuje úvěrové, protistranové, operační a koncentrační riziko. Fond není pojištěný jako bankovní vklad.",
    minInvestment: "od 100 Kč",
    planningRate: 4,
    officialPerformance: {
      ytd: "0.66%",
      oneYear: "2.79%",
      threeYearPA: "4.26% (derived from official 13.32% cumulative / 3y)",
      fiveYearPA: null,
      tenYearPA: null,
      sinceInceptionPA: "4.54% (derived from official 17.47% cumulative since 2022-08-16)",
      asOf: "2026-04-01",
    },
    benefits: [
      "Velmi nízká rizikovost s SRI 1/7.",
      "Rychlá dostupnost peněz a nízká minimální investice.",
      "Výnos navázaný na repo sazbu ČNB bez limitů běžných pro spořicí účty.",
    ],
    parameters: {
      fundType: "Speciální fond kolektivního investování, otevřený podílový fond",
      launchDate: "2022-08-16",
      currentAnnualReturn: "2.80%",
      targetReturn: "3 - 5 % ročně",
      linkedRate: "2W repo sazba ČNB minus cca 0.7 p. b.",
      dealingFrequency: "Denní",
      redemptionSettlement: "obvykle do 3 pracovních dnů",
      entryFee: "max. 0.5 %",
      exitFee: "0 %",
      managementFee: "0.35%",
      administrationFee: "0.05%",
      fundCapital: "3 381 812 578.09 CZK",
      official3YCumulative: "13.32%",
      officialSinceInceptionCumulative: "17.47%",
    },
    topHoldings: ["Repo operace", "Termínované vklady", "Overnight depozita"],
    countries: [],
    sectors: ["Peněžní trh", "Repo operace", "Bankovní vklady"],
    morningstarRating: null,
    awards: [],
    factsheetUrl: "https://www.investika.cz/investicni-fondy/monetika",
    factsheetAsOf: "2026-04-01",
    verifiedAt: "2026-04-05",
    logo: "/logos/funds/monetika.png",
    heroImage: "/report-assets/funds/monetika/hero.svg",
    galleryType: "logo",
    galleryImages: [
      "/report-assets/funds/monetika/gallery-1.png",
      "/report-assets/funds/monetika/gallery-2.png",
      "/report-assets/funds/monetika/gallery-3.png",
    ],
    sources: [
      {
        label: "Official product page",
        url: "https://www.investika.cz/investicni-fondy/monetika",
        kind: "landing_page",
      },
      {
        label: "Official current KID CZK",
        url: "https://www.investika.cz/files/monetika-kid-czk-20260211.pdf",
        kind: "kid",
      },
      {
        label: "Official statute",
        url: "https://www.investika.cz/files/statut-monetika-120226-final.pdf",
        kind: "documents",
      },
    ],
    assetTodo: ["hero"],
    notes: [
      "Logo: PNG v `public/logos/funds/` — při změně brand guidelines aktualizovat soubor.",
      "Investika veřejně reportuje 3Y a since inception primárně jako kumulativní výkon; anualizace výše je dopočtena pro potřeby jednotného seed formátu.",
      "planningRate 4 % je interní modelový předpoklad pro projekce, ne oficiální performance.",
    ],
  },
  {
    baseFundKey: "efektika",
    canonicalName: "EFEKTIKA, otevřený podílový fond, třída CZK měnově nezajištěná",
    displayName: "EFEKTIKA",
    provider: "INVESTIKA",
    manager: "INVESTIKA, investiční společnost, a.s.",
    category: "Akciový fond",
    subcategory: "Index-oriented / ETF wrapper",
    currency: "CZK",
    isin: "CZ0008477650",
    ticker: null,
    riskSRI: 5,
    goal:
      "Fond má za cíl nabídnout podílníkům participaci na vývoji vyspělých kapitálových trhů v Severní Americe, Evropě a Asii prostřednictvím investování do investičních cenných papírů a ETF navázaných na významné akciové indexy.",
    strategy:
      "Podle statutu investuje významnou část portfolia do akciových fondů a ETF kopírujících akciové indexy. Produktová stránka jej rámuje jako jednoduchou cestu k akciovým trhům a k expozici na velké globální společnosti.",
    description:
      "Růstově orientovaný fond pro dlouhodobé investování. Produktově je komunikován hlavně jako snadná cesta k firmám z amerických akciových indexů, zejména S&P 500, bez nutnosti kupovat jednotlivé akcie napřímo.",
    suitable:
      "Pro investory s delším horizontem, kteří akceptují vyšší kolísání a chtějí růstovou akciovou složku portfolia.",
    horizon: "5+ let",
    liquidity: "Denní oceňování a obchodování; otevřený UCITS fond.",
    risks:
      "Vyšší kolísání hodnoty podílového listu v návaznosti na vývoj akciových trhů, měnové riziko u nezajištěné třídy a rizika cílových ETF / indexových strategií.",
    minInvestment: "od 100 Kč",
    planningRate: 9,
    officialPerformance: {
      ytd: "-3.45%",
      oneYear: "7.80%",
      threeYearPA: null,
      fiveYearPA: null,
      tenYearPA: null,
      sinceInceptionPA: "9.84% (derived from official 34.13% cumulative since 2023-02-14)",
      asOf: "2026-03-31",
    },
    benefits: [
      "Jednoduchá cesta k růstovým akciovým trhům od malých částek.",
      "Dostupný i pro běžné investory – obchoduje se každý den a není potřeba kupovat jednotlivé akcie.",
      "Vhodné jako růstová složka dlouhodobého portfolia nebo DIP řešení.",
    ],
    parameters: {
      fundType: "Standardní fond kolektivního investování, otevřený podílový fond (UCITS)",
      launchDate: "2023-02-14",
      dealingFrequency: "Denní",
      recommendedHoldingPeriod: "5 a více let",
      entryFee: "max. 5 %",
      exitFee: "0 %",
      managementFee: "1.55%",
      administrationFee: "0.15%",
      targetReturnMarketing: "9 % ročně*",
      targetReturnNote:
        "Historické zhodnocení indexu S&P 500 po zohlednění nákladů dle marketingové komunikace správce.",
      fundCapital: "711 660 947.57 CZK",
      officialSinceInceptionCumulative: "34.13%",
      portfolioInvestmentInstruments: "94.41% (2026-03-31)",
      bankDeposits: "4.22% (2026-03-31)",
    },
    topHoldings: ["Apple", "Microsoft", "Amazon"],
    countries: [],
    sectors: [],
    morningstarRating: null,
    awards: [],
    factsheetUrl: "https://www.investika.cz/investicni-fondy/efektika",
    factsheetAsOf: "2026-03-31",
    verifiedAt: "2026-04-05",
    logo: "/logos/funds/efektika.png",
    heroImage: "/report-assets/funds/efektika/hero.svg",
    galleryType: "logo",
    galleryImages: [
      "/report-assets/funds/efektika/gallery-1.svg",
      "/report-assets/funds/efektika/gallery-2.svg",
      "/report-assets/funds/efektika/gallery-3.svg",
    ],
    sources: [
      {
        label: "Official product page",
        url: "https://www.investika.cz/investicni-fondy/efektika",
        kind: "landing_page",
      },
      {
        label: "Official KID CZK unhedged",
        url: "https://www.investika.cz/files/efektika-kid-czk-menove-nezajistena.pdf",
        kind: "kid",
      },
      {
        label: "Official statute",
        url: "https://www.investika.cz/files/20250625-statut-efektika-clean.pdf",
        kind: "documents",
      },
      {
        label: "Official leaflet",
        url: "https://www.investika.cz/files/efektika-letak-rgb-20240603.pdf",
        kind: "factsheet",
      },
    ],
    assetTodo: ["hero"],
    notes: [
      "Logo: PNG v `public/logos/funds/` — při změně brand guidelines aktualizovat soubor.",
      "TopHoldings obsahují jen veřejně marketingově komunikované příklady firem z produktové stránky, nikoli plný oficiální holdings list s vahami.",
      "Statut je širší než samotná marketingová komunikace a připouští expozici i na další hlavní indexy mimo čistý S&P 500; v UI je vhodné držet popis opatrně a přesně.",
      "planningRate 9 % je interní modelový předpoklad pro projekce, ne oficiální performance.",
    ],
  },
];
